import { ProjectConfig } from "./types";
// Research-partner logos for the landing strip. Same five assets the landing
// previously hardcoded (constants/images) — now moved into project config so the
// strip is per-deployment (Outborn/default show none).
import {
  LOGOECEVE,
  LOGOINSERM,
  LOGOPARISUNI,
  LOGORECHERCHE,
  LOGOSANTE,
} from "../constants/images";
import logoInserm from "../assets/MentalPlus/LogoInserm.png";
import logoECEVE from "../assets/MentalPlus/logoECEVE.png";
import logoUPC from "../assets/MentalPlus/logoUPC.png";
import logoAPHP from "../assets/MentalPlus/logoAPHP.gif";
import logoMinistereSante from "../assets/MentalPlus/logoMinistereSante.png";
import logoMinistereEduc from "../assets/MentalPlus/LogoMinistereEduc.png";
import logoAXA from "../assets/MentalPlus/logoAXA.png";

export const mentalplus: ProjectConfig = {
  slug: "mentalplus",
  homeExperience: "island-map",
  // "resources" = the researcher-managed article library (ARTICLE contents).
  // NOTE: "evolution" is intentionally NOT in the tab bar — its route/page stay
  // registered in Tabs.tsx (reachable from the island detail, and reusable
  // elsewhere), it just no longer gets a bottom-tab button.
  tabs: ["home", "videos", "helps", "resources", "account"],
  // Header slots: burger takes the LEFT corner (where the logo normally sits —
  // Mental+'s identity is already carried by the welcome BigLogo and the island
  // art), and the right corner holds the round 3114 call button on every page.
  // 3114 is the national suicide-prevention line — the same number pinned at
  // the top of the "Ma bouée" helps tab, one tap away from anywhere in the app.
  // Pages still override per-view: island detail swaps left for the back pill,
  // and the island map adds the visibility-settings trigger before the call
  // button (see IslandMap.tsx).
  header: {
    left: ["menu"],
    right: ["call"],
    callNumber: "3114",
  },
  // PKs of THIS deployment's `sequences` table — deployment data, not shared
  // code. IslandRegistry and the login/consent prefetch both read from here.
  islandSequenceIds: {
    amis: { main: 10, inline: 11 },
    famille: { main: 8, inline: 9 },
    amour: { main: 14, inline: 15 },
    ecrans: { main: 6, inline: 7 },
    loisirs: { main: 16, inline: 17 },
    moi: { main: 12, inline: 13 },
  },
  // Onboarding (incl. video stories) is now authored per-study in Diapason —
  // no hard-coded video ids here.
  // Mental+ visual identity mapped onto the role semantics (tokens.ts):
  // primary = chrome, painted directly -> the historical pastel header/toolbar
  // color; secondary = CTAs/badges -> the hardcoded yellow; accent = answer
  // controls/links -> the historical Ionic blue. So the real look survives:
  // pastel header, yellow buttons, blue radios/checkboxes/links, and the
  // strong teal for progress fills derives from the pastel (deriveEmphasis
  // gives #2cb9b6 ≈ the historical #2db6b4). The tab bar is the shared flat
  // primary dock like every other study.
  theme: {
    colors: {
      primary: "#94f1ef",
      secondary: "#fecf48",
      accent: "#4285F4",
      background: "#ffffff",
      surface: "#CDEBF3",
      text: "#041723",
    },
    font: "Ciutadella Rounded",
  },
  // Landing copy — Mental+ is a well-being APP, not a study, so the wording is warm
  // and personal (not clinical): no badge pill, no confidentiality note (kept lean),
  // and the sign-up button says "Découvrir" instead of the shared "S'inscrire à
  // l'étude !". {{appName}} resolves to the server APP_NAME.
  welcome: {
    fr: {
      title: "Bienvenue dans {{appName}}",
      tagline:
        "Fais le point sur ton bien-être, à ton rythme, en toute confidentialité.",
      registerLabel: "Découvrir",
    },
    en: {
      title: "Welcome to {{appName}}",
      tagline:
        "Check in on how you feel, at your own pace and in confidence.",
      registerLabel: "Discover",
    },
  },
  // Institutional backing strip on the landing (the five funders the landing used
  // to hardcode). caption follows the app language via the i18n key.
  partnerLogos: {
    logos: [LOGOECEVE, LOGOINSERM, LOGOPARISUNI, LOGOSANTE, LOGORECHERCHE],
    captionKey: "home_partners_caption",
  },
  // "Ma bouée" defaults — France, adolescent population. Format "Title – Number"
  // (EN-DASH separator; existing users' stored data uses the same format).
  helps: {
    pinned: "Prévention suicide – 3114",
    urgences: [
      "Prévention suicide – 3114",
      "Urgence vitale – 112",
      "Croix-Rouge écoute – 0 800 858 858",
      "SOS Amitié – 09 72 39 40 50",
      "SOS crise – 0800 19 00 00",
    ],
    utiles: [
      "Fil Santé Jeunes – 0 800 235 236",
      "Planning familial – 0 800 08 11 11",
      "Net Écoute – 3018",
    ],
  },
  // "Qui sommes-nous ?" — TODO: real study description (placeholder text kept
  // from the previous hardcoded modal).
  about: {
    title: "Qui sommes-nous ?",
    paragraphs: [
      "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    ],
    logosTop: [logoInserm, logoECEVE, logoUPC, logoAPHP],
    logosBottom: [logoMinistereSante, logoMinistereEduc, logoAXA],
  },
  // Videos filter sheet. Entries go through t(): keys resolve, literals pass
  // through. Resolved strings are matched VERBATIM against Cloudflare video
  // tags — do not reword without retagging the videos.
  videoFilterSections: [
    {
      label: "mentalplus_filtre_ile",
      chips: [
        "mentalplus_tab_ile_amour",
        "mentalplus_tab_ile_amis",
        "mentalplus_tab_ile_moi",
        "mentalplus_tab_ile_ecrans",
        "mentalplus_tab_ile_famille",
      ],
    },
    {
      label: "mentalplus_filtre_categories",
      chips: [
        "mentalplus_filtre_categories_explications",
        "mentalplus_filtre_categories_solutions",
      ],
    },
    {
      label: "mentalplus_filtre_trier",
      chips: [
        "mentalplus_filtre_trier_popularite",
        "mentalplus_filtre_trier_recent",
        "mentalplus_filtre_trier_ancien",
      ],
    },
  ],
};
